import { useState } from 'react';
import { Card, Button, Input, Badge, Label } from '../ui';
import { Plus, Pencil, Trash2, Check, X } from 'lucide-react';
import type { Profile, ActScore, SatScore } from '@scholarship-app/types';

interface Props {
  testScores: Profile['test_scores'];
  editing: boolean;
  onChange: (next: Profile['test_scores']) => void;
}

const emptyActScore = (): ActScore => ({
  score: 0,
  date: '',
  section_scores: { english: 0, math: 0, reading: 0, science: 0 },
  is_superscore: false,
});

const emptySatScore = (): SatScore => ({
  score: 0,
  date: '',
  section_scores: { ebrw: 0, math: 0 },
  is_superscore: false,
});

export function ProfileTestScoresEditor({ testScores, editing, onChange }: Props) {
  const [addingType, setAddingType] = useState<'act' | 'sat' | null>(null);
  const [editIndex, setEditIndex] = useState<{ type: 'act' | 'sat'; index: number } | null>(null);
  const [draft, setDraft] = useState<ActScore | SatScore | null>(null);

  function startAdd(type: 'act' | 'sat') {
    setAddingType(type);
    setEditIndex(null);
    setDraft(type === 'act' ? emptyActScore() : emptySatScore());
  }
  function startEdit(type: 'act' | 'sat', index: number) {
    setAddingType(null);
    setEditIndex({ type, index });
    const score = type === 'act' ? testScores.act[index] : testScores.sat[index];
    setDraft({ ...score });
  }
  function cancel() {
    setAddingType(null);
    setEditIndex(null);
    setDraft(null);
  }
  function save() {
    if (!draft) return;
    if (addingType === 'act') {
      onChange({ ...testScores, act: [...testScores.act, draft as ActScore] });
    } else if (addingType === 'sat') {
      onChange({ ...testScores, sat: [...testScores.sat, draft as SatScore] });
    } else if (editIndex) {
      if (editIndex.type === 'act') {
        const next = [...testScores.act];
        next[editIndex.index] = draft as ActScore;
        onChange({ ...testScores, act: next });
      } else {
        const next = [...testScores.sat];
        next[editIndex.index] = draft as SatScore;
        onChange({ ...testScores, sat: next });
      }
    }
    cancel();
  }
  function remove(type: 'act' | 'sat', index: number) {
    if (!confirm('Delete this test score entry?')) return;
    if (type === 'act') {
      onChange({ ...testScores, act: testScores.act.filter((_, i) => i !== index) });
    } else {
      onChange({ ...testScores, sat: testScores.sat.filter((_, i) => i !== index) });
    }
  }

  const isEditing = (type: 'act' | 'sat', i: number) =>
    editIndex && editIndex.type === type && editIndex.index === i;

  return (
    <div className="space-y-4">
      {/* ACT scores */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-soft">ACT</span>
          {editing && (
            <Button type="button" size="sm" variant="ghost" onClick={() => startAdd('act')}>
              <Plus size={12} className="mr-1" />Add ACT
            </Button>
          )}
        </div>
        {testScores.act.length === 0 && addingType !== 'act' && (
          <p className="text-sm text-soft">No ACT scores yet.</p>
        )}
        <div className="space-y-2">
          {testScores.act.map((s, i) => (
            <div key={i}>
              {isEditing('act', i) && draft ? (
                <ActEditor draft={draft as ActScore} setDraft={setDraft as (d: ActScore) => void} onSave={save} onCancel={cancel} />
              ) : (
                <div className="flex items-center justify-between bg-cream rounded-md px-3 py-2">
                  <div className="text-sm">
                    <strong>{s.score}</strong> on {s.date || 'unknown date'}
                    {s.is_superscore && <Badge variant="indigo" className="ml-2">Superscore</Badge>}
                    <div className="text-xs text-soft">
                      English {s.section_scores.english}, Math {s.section_scores.math}, Reading {s.section_scores.reading}, Science {s.section_scores.science}
                    </div>
                  </div>
                  {editing && (
                    <div className="flex gap-1">
                      <Button type="button" size="sm" variant="ghost" onClick={() => startEdit('act', i)}><Pencil size={12} /></Button>
                      <Button type="button" size="sm" variant="ghost" onClick={() => remove('act', i)}><Trash2 size={12} /></Button>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
          {addingType === 'act' && draft && (
            <ActEditor draft={draft as ActScore} setDraft={setDraft as (d: ActScore) => void} onSave={save} onCancel={cancel} />
          )}
        </div>
        {editing && (
          <label className="flex items-center gap-2 mt-2 text-sm text-soft">
            <input
              type="checkbox"
              checked={testScores.act_retake_planned}
              onChange={(e) => onChange({ ...testScores, act_retake_planned: e.target.checked })}
            />
            ACT retake planned
          </label>
        )}
      </div>

      {/* SAT scores */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-soft">SAT</span>
          {editing && (
            <Button type="button" size="sm" variant="ghost" onClick={() => startAdd('sat')}>
              <Plus size={12} className="mr-1" />Add SAT
            </Button>
          )}
        </div>
        {testScores.sat.length === 0 && addingType !== 'sat' && (
          <p className="text-sm text-soft">No SAT scores yet.</p>
        )}
        <div className="space-y-2">
          {testScores.sat.map((s, i) => (
            <div key={i}>
              {isEditing('sat', i) && draft ? (
                <SatEditor draft={draft as SatScore} setDraft={setDraft as (d: SatScore) => void} onSave={save} onCancel={cancel} />
              ) : (
                <div className="flex items-center justify-between bg-cream rounded-md px-3 py-2">
                  <div className="text-sm">
                    <strong>{s.score}</strong> on {s.date || 'unknown date'}
                    <div className="text-xs text-soft">EBRW {s.section_scores.ebrw}, Math {s.section_scores.math}</div>
                  </div>
                  {editing && (
                    <div className="flex gap-1">
                      <Button type="button" size="sm" variant="ghost" onClick={() => startEdit('sat', i)}><Pencil size={12} /></Button>
                      <Button type="button" size="sm" variant="ghost" onClick={() => remove('sat', i)}><Trash2 size={12} /></Button>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
          {addingType === 'sat' && draft && (
            <SatEditor draft={draft as SatScore} setDraft={setDraft as (d: SatScore) => void} onSave={save} onCancel={cancel} />
          )}
        </div>
        {editing && (
          <label className="flex items-center gap-2 mt-2 text-sm text-soft">
            <input
              type="checkbox"
              checked={testScores.sat_planned}
              onChange={(e) => onChange({ ...testScores, sat_planned: e.target.checked })}
            />
            SAT planned
          </label>
        )}
      </div>
    </div>
  );
}

function ActEditor({ draft, setDraft, onSave, onCancel }: { draft: ActScore; setDraft: (d: ActScore) => void; onSave: () => void; onCancel: () => void }) {
  return (
    <Card className="p-3 bg-forest-50/40 border-forest-200 space-y-2">
      <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
        <div>
          <Label>Composite</Label>
          <Input type="number" min="1" max="36" value={draft.score || ''} onChange={(e) => setDraft({ ...draft, score: Number(e.target.value) })} />
        </div>
        <div>
          <Label>Date</Label>
          <Input type="date" value={draft.date} onChange={(e) => setDraft({ ...draft, date: e.target.value })} />
        </div>
        <div className="flex items-center gap-2 mt-5">
          <input type="checkbox" id="superscore" checked={draft.is_superscore} onChange={(e) => setDraft({ ...draft, is_superscore: e.target.checked })} />
          <label htmlFor="superscore" className="text-sm">Superscore</label>
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <div>
          <Label>English</Label>
          <Input type="number" min="1" max="36" value={draft.section_scores.english || ''} onChange={(e) => setDraft({ ...draft, section_scores: { ...draft.section_scores, english: Number(e.target.value) } })} />
        </div>
        <div>
          <Label>Math</Label>
          <Input type="number" min="1" max="36" value={draft.section_scores.math || ''} onChange={(e) => setDraft({ ...draft, section_scores: { ...draft.section_scores, math: Number(e.target.value) } })} />
        </div>
        <div>
          <Label>Reading</Label>
          <Input type="number" min="1" max="36" value={draft.section_scores.reading || ''} onChange={(e) => setDraft({ ...draft, section_scores: { ...draft.section_scores, reading: Number(e.target.value) } })} />
        </div>
        <div>
          <Label>Science</Label>
          <Input type="number" min="1" max="36" value={draft.section_scores.science || ''} onChange={(e) => setDraft({ ...draft, section_scores: { ...draft.section_scores, science: Number(e.target.value) } })} />
        </div>
      </div>
      <div className="flex justify-end gap-2">
        <Button type="button" size="sm" variant="secondary" onClick={onCancel}><X size={12} className="mr-1" />Cancel</Button>
        <Button type="button" size="sm" onClick={onSave}><Check size={12} className="mr-1" />Save</Button>
      </div>
    </Card>
  );
}

function SatEditor({ draft, setDraft, onSave, onCancel }: { draft: SatScore; setDraft: (d: SatScore) => void; onSave: () => void; onCancel: () => void }) {
  return (
    <Card className="p-3 bg-forest-50/40 border-forest-200 space-y-2">
      <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
        <div>
          <Label>Total</Label>
          <Input type="number" min="400" max="1600" value={draft.score || ''} onChange={(e) => setDraft({ ...draft, score: Number(e.target.value) })} />
        </div>
        <div>
          <Label>EBRW</Label>
          <Input type="number" min="200" max="800" value={draft.section_scores.ebrw || ''} onChange={(e) => setDraft({ ...draft, section_scores: { ...draft.section_scores, ebrw: Number(e.target.value) } })} />
        </div>
        <div>
          <Label>Math</Label>
          <Input type="number" min="200" max="800" value={draft.section_scores.math || ''} onChange={(e) => setDraft({ ...draft, section_scores: { ...draft.section_scores, math: Number(e.target.value) } })} />
        </div>
        <div>
          <Label>Date</Label>
          <Input type="date" value={draft.date} onChange={(e) => setDraft({ ...draft, date: e.target.value })} />
        </div>
        <div className="flex items-center gap-2 mt-5">
          <input type="checkbox" id="sat_superscore" checked={draft.is_superscore} onChange={(e) => setDraft({ ...draft, is_superscore: e.target.checked })} />
          <label htmlFor="sat_superscore" className="text-sm">Superscore</label>
        </div>
      </div>
      <div className="flex justify-end gap-2">
        <Button type="button" size="sm" variant="secondary" onClick={onCancel}><X size={12} className="mr-1" />Cancel</Button>
        <Button type="button" size="sm" onClick={onSave}><Check size={12} className="mr-1" />Save</Button>
      </div>
    </Card>
  );
}
